import { CompositionStage, useComposition, animate, Easing, clamp } from "@/components/hero/stage";

/* Authored composition — fitness & nutrition: the load, the fuel, the twelve week climb. */

const INK = "#F1F7EE";
const MUTE = "rgba(226,240,222,0.62)";
const FAINT = "rgba(226,240,222,0.30)";
const FONT = "Outfit, Poppins, system-ui, sans-serif";
const SCENES = [
  { name: "Load", dur: 5 },
  { name: "Fuel", dur: 5 },
  { name: "Climb", dur: 5.5 },
];

const MOTION = {
  enter: (start, from, to, d) => animate({ from, to, start, end: start + (d || 0.7), ease: Easing.easeOutCubic }),
  draw: (start, end, from, to) => animate({ from: from == null ? 0 : from, to: to == null ? 1 : to, start, end, ease: Easing.easeInOutCubic }),
  pop: (start, from, to, d) => animate({ from, to, start, end: start + (d || 0.5), ease: Easing.easeOutBack }),
};

const fade = (T, start, end, d = 0.45) => Math.min(clamp((T - start) / d, 0, 1), clamp((end - T) / d, 0, 1));

const PLATES = [
  { h: 196, w: 34, kg: 20, gap: 64, c: 0.95 },
  { h: 158, w: 28, kg: 15, gap: 100, c: 0.78 },
  { h: 124, w: 23, kg: 10, gap: 132, c: 0.62 },
  { h: 92, w: 19, kg: 5, gap: 161, c: 0.48 },
];

function LoadLayer({ T, cue, end, accent }) {
  const o = fade(T, cue, end);
  const local = T - cue;
  const repDur = 1.0;
  const repIdx = Math.max(0, Math.floor((local - 0.75) / repDur));
  const ph = clamp((local - 0.75) / repDur - repIdx, 0, 1);
  const lift = local > 0.75 ? (-52 * (1 - Math.cos(ph * Math.PI * 2))) / 2 : 0;
  const shown = clamp(Math.floor((local - 0.12) / 0.36) + 1, 0, 4);
  const kg = 20 + PLATES.slice(0, shown).reduce((s, p) => s + p.kg * 2, 0);
  const barY = 268;

  return (
    <div style={{ position: "absolute", inset: 0, opacity: o, pointerEvents: "none" }}>
      <div style={{ position: "absolute", left: 0, top: 8, font: "500 15px/1 " + FONT, letterSpacing: "0.16em", color: MUTE }}>SESSION 14 · LOWER BODY</div>
      <div style={{ position: "absolute", left: 0, top: barY + lift, width: 690, height: 0 }}>
        <div style={{ position: "absolute", left: 62, top: -6, width: 596, height: 12, borderRadius: 6, background: "linear-gradient(90deg,rgba(226,240,222,0.35),rgba(226,240,222,0.75),rgba(226,240,222,0.35))" }} />
        <div style={{ position: "absolute", left: 300, top: -17, width: 120, height: 34, borderRadius: 8, background: "rgba(226,240,222,0.10)" }} />
        {PLATES.map((p, i) => {
          const at = cue + 0.12 + i * 0.36;
          const t = MOTION.pop(at, 0, 1, 0.5)(T);
          return [1, -1].map((side) => (
            <div key={i + "" + side} style={{
              position: "absolute", left: 360 + side * p.gap - p.w / 2, top: -p.h / 2,
              width: p.w, height: p.h, borderRadius: p.w / 2 + "px",
              background: i === 0 ? accent : "rgba(226,240,222," + (0.1 + p.c * 0.16) + ")",
              border: i === 0 ? "none" : "1.5px solid rgba(226,240,222," + (0.18 + p.c * 0.22) + ")",
              opacity: clamp(t, 0, 1),
              transform: "translateX(" + (1 - clamp(t, 0, 1)) * side * 90 + "px) scaleY(" + (0.7 + 0.3 * clamp(t, 0, 1)) + ")",
            }} />
          ));
        })}
      </div>
      <div style={{ position: "absolute", left: 0, top: 430, display: "flex", alignItems: "flex-end", gap: 16 }}>
        <div style={{ font: "600 84px/0.9 " + FONT, color: INK, letterSpacing: "-0.03em", fontVariantNumeric: "tabular-nums" }}>{kg}</div>
        <div style={{ font: "500 22px/1.6 " + FONT, color: MUTE }}>kg · back squat</div>
      </div>
      <div style={{ position: "absolute", left: 0, top: 540, display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ font: "500 14px/1 " + FONT, letterSpacing: "0.16em", color: FAINT }}>REPS</div>
        <div style={{ display: "flex", gap: 10 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const done = repIdx > i || (repIdx === i && ph > 0.55);
            const active = repIdx === i && ph <= 0.55;
            return <div key={i} style={{ width: 30, height: 6, borderRadius: 3, background: done ? accent : active ? "rgba(226,240,222,0.55)" : "rgba(226,240,222,0.16)" }} />;
          })}
        </div>
        <div style={{ font: "500 15px/1 " + FONT, color: MUTE, marginLeft: 8 }}>SET 3 / 5 · RPE 8</div>
      </div>
    </div>
  );
}

const MACROS = [
  { k: "PROTEIN", g: 186, p: 0.88 },
  { k: "CARBS", g: 240, p: 0.72 },
  { k: "FAT", g: 68, p: 0.61 },
];

function Ring({ p, color }) {
  const r = 62, C = 2 * Math.PI * r;
  return (
    <svg width="150" height="150" viewBox="0 0 150 150">
      <circle cx="75" cy="75" r={r} fill="none" stroke="rgba(226,240,222,0.13)" strokeWidth="13" />
      <circle cx="75" cy="75" r={r} fill="none" stroke={color} strokeWidth="13" strokeLinecap="round"
        strokeDasharray={C} strokeDashoffset={C * (1 - p)} transform="rotate(-90 75 75)" />
    </svg>
  );
}

function FuelLayer({ T, cue, end, accent }) {
  const o = fade(T, cue, end);
  const kcal = Math.round(MOTION.draw(cue + 0.4, cue + 2.6, 0, 2480)(T) / 10) * 10;
  const barTotal = MOTION.draw(cue + 0.5, cue + 2.4, 0, 1)(T);

  return (
    <div style={{ position: "absolute", inset: 0, opacity: o, pointerEvents: "none" }}>
      <div style={{ position: "absolute", left: 0, top: 8, font: "500 15px/1 " + FONT, letterSpacing: "0.16em", color: MUTE }}>FUEL FOR SESSION 14</div>
      <div style={{ position: "absolute", left: 0, top: 66, display: "flex", gap: 34 }}>
        {MACROS.map((m, i) => {
          const at = cue + 0.35 + i * 0.32;
          const p = MOTION.draw(at, at + 1.5, 0, m.p)(T);
          const rise = MOTION.enter(at, 26, 0, 0.8)(T);
          return (
            <div key={m.k} style={{ transform: "translateY(" + rise + "px)", opacity: clamp((T - at) / 0.5, 0, 1) }}>
              <div style={{ position: "relative", width: 150, height: 150 }}>
                <Ring p={p} color={i === 0 ? accent : "rgba(226,240,222," + (0.72 - i * 0.18) + ")"} />
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ font: "600 34px/1 " + FONT, color: INK, fontVariantNumeric: "tabular-nums" }}>{Math.round(m.g * (p / m.p || 0))}</div>
                  <div style={{ font: "500 14px/1.8 " + FONT, color: FAINT }}>grams</div>
                </div>
              </div>
              <div style={{ font: "500 13px/1 " + FONT, letterSpacing: "0.16em", color: MUTE, textAlign: "center", marginTop: 12 }}>{m.k}</div>
            </div>
          );
        })}
      </div>
      <div style={{ position: "absolute", left: 0, top: 400, display: "flex", alignItems: "flex-end", gap: 16 }}>
        <div style={{ font: "600 84px/0.9 " + FONT, color: INK, letterSpacing: "-0.03em", fontVariantNumeric: "tabular-nums" }}>{kcal.toLocaleString("en-US")}</div>
        <div style={{ font: "500 22px/1.6 " + FONT, color: MUTE }}>kcal today</div>
      </div>
      <div style={{ position: "absolute", left: 0, top: 520, width: 620 }}>
        <div style={{ display: "flex", gap: 5, height: 14 }}>
          {MACROS.map((m, i) => {
            const share = [0.34, 0.44, 0.22][i];
            return <div key={m.k} style={{ width: share * 620 * barTotal + "px", height: 14, borderRadius: 7, background: i === 0 ? accent : "rgba(226,240,222," + (0.42 - i * 0.14) + ")" }} />;
          })}
        </div>
        <div style={{ font: "500 15px/1 " + FONT, color: MUTE, marginTop: 18 }}>Four meals, written against the plan — not a calorie ceiling.</div>
      </div>
    </div>
  );
}

const CURVE = [8, 15, 14, 24, 31, 29, 42, 50, 47, 62, 71, 80];
const PHASES = [["Foundation", "1–3"], ["Load", "4–6"], ["Build", "7–9"], ["Retest", "10–12"]];
const CW = 620, CH = 330, CTOP = 92;

function ClimbLayer({ T, cue, end, accent }) {
  const o = fade(T, cue, end);
  const p = MOTION.draw(cue + 0.5, cue + 3.6, 0, 1)(T);
  const px = (i) => (i / 11) * CW;
  const py = (v) => CH - (v / 90) * CH;
  const pos = p * 11;
  const lo = Math.floor(pos), hi = Math.min(11, lo + 1), f = pos - lo;
  const cx = px(lo) + (px(hi) - px(lo)) * f;
  const cy = py(CURVE[lo] + (CURVE[hi] - CURVE[lo]) * f);
  let pts = "";
  for (let i = 0; i <= lo; i++) pts += px(i) + "," + py(CURVE[i]) + " ";
  pts += cx + "," + cy;
  const week = Math.min(12, Math.max(1, Math.round(1 + pos)));
  const activePhase = clamp(Math.floor((week - 1) / 3), 0, 3);
  const badge = MOTION.pop(cue + 3.5, 0, 1, 0.6)(T);

  return (
    <div style={{ position: "absolute", inset: 0, opacity: o, pointerEvents: "none" }}>
      <div style={{ position: "absolute", left: 0, top: 8, font: "500 15px/1 " + FONT, letterSpacing: "0.16em", color: MUTE }}>TWELVE WEEK RECONSTRUCTION</div>
      <div style={{ position: "absolute", left: 0, top: CTOP, width: CW, height: CH }}>
        <svg width={CW} height={CH} viewBox={"0 0 " + CW + " " + CH} style={{ overflow: "visible" }}>
          {[0, 1, 2, 3].map((i) => <line key={"h" + i} x1="0" y1={(CH / 3) * i} x2={CW} y2={(CH / 3) * i} stroke="rgba(226,240,222,0.10)" strokeWidth="1" />)}
          {CURVE.map((v, i) => <line key={"v" + i} x1={px(i)} y1={CH} x2={px(i)} y2={CH + 10} stroke="rgba(226,240,222,0.18)" strokeWidth="1" />)}
          <polyline points={pts + " " + cx + "," + CH + " 0," + CH} fill={accent} opacity="0.10" stroke="none" />
          <polyline points={pts} fill="none" stroke={accent} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={cx} cy={cy} r="14" fill={accent} opacity="0.22" />
          <circle cx={cx} cy={cy} r="6.5" fill={accent} />
        </svg>
      </div>
      <div style={{ position: "absolute", left: 0, top: CTOP + CH + 26, width: CW, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {PHASES.map((ph, i) => {
          const on = i <= activePhase;
          return (
            <div key={ph[0]} style={{ borderTop: "2px solid " + (i === activePhase ? accent : on ? "rgba(226,240,222,0.35)" : "rgba(226,240,222,0.12)"), paddingTop: 12 }}>
              <div style={{ font: "600 18px/1.2 " + FONT, color: on ? INK : FAINT }}>{ph[0]}</div>
              <div style={{ font: "500 13px/1.6 " + FONT, letterSpacing: "0.1em", color: i === activePhase ? accent : FAINT }}>WEEKS {ph[1]}</div>
            </div>
          );
        })}
      </div>
      <div style={{
        position: "absolute", left: 0, top: 540, display: "inline-flex", alignItems: "center", gap: 12,
        padding: "12px 22px", borderRadius: 999, background: "rgba(169,227,107,0.14)", border: "1px solid rgba(169,227,107,0.32)",
        opacity: clamp(badge, 0, 1), transform: "scale(" + (0.9 + 0.1 * clamp(badge, 0, 1)) + ")",
      }}>
        <div style={{ width: 8, height: 8, borderRadius: 4, background: accent }} />
        <div style={{ font: "600 18px/1 " + FONT, color: INK }}>+18% back squat · retested week 12</div>
      </div>
    </div>
  );
}

function Plan({ accent }) {
  const { T, CUES, authoredTotal: total } = useComposition();
  const scenes = [
    { cue: CUES.Load, end: CUES.Fuel },
    { cue: CUES.Fuel, end: CUES.Climb },
    { cue: CUES.Climb, end: total },
  ];
  const idx = T < CUES.Fuel ? 0 : T < CUES.Climb ? 1 : 2;
  const pulse = 0.5 + 0.5 * Math.sin((T / total) * Math.PI * 2 * 3);
  const weekLabel = idx === 2 ? "WEEKS 01 – 12" : "WEEK 05 OF 12";

  return (
    <div style={{ position: "absolute", inset: 0, fontFamily: FONT, overflow: "hidden", boxSizing: "border-box" }}>
      <div style={{ position: "absolute", left: 0, right: 0, top: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 9, height: 9, borderRadius: 5, background: accent, opacity: 0.5 + pulse * 0.5 }} />
          <div style={{ font: "600 15px/1 " + FONT, letterSpacing: "0.18em", color: INK }}>{weekLabel}</div>
        </div>
        <div style={{ display: "flex", gap: 7 }}>
          {[0, 1, 2].map((i) => <div key={i} style={{ width: i === idx ? 24 : 8, height: 8, borderRadius: 4, background: i === idx ? accent : "rgba(226,240,222,0.22)" }} />)}
        </div>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, top: 66, bottom: 0 }}>
        <LoadLayer T={T} cue={scenes[0].cue} end={scenes[0].end} accent={accent} />
        <FuelLayer T={T} cue={scenes[1].cue} end={scenes[1].end} accent={accent} />
        <ClimbLayer T={T} cue={scenes[2].cue} end={scenes[2].end} accent={accent} />
      </div>
    </div>
  );
}

export default function FitnessHero({ accent = "#A9E36B" }) {
  return (
    <CompositionStage width={700} height={700} scenes={SCENES} minScale={0.56} maxHeight={620}>
      <Plan accent={accent} />
    </CompositionStage>
  );
}
